import { makeStyles, tokens, Tooltip } from '@fluentui/react-components';
import {
  CalendarRegular,
  WarningRegular,
  DocumentRegular,
  ClipboardTaskListLtrRegular,
  EditRegular,
  EyeRegular,
  ErrorCircleRegular,
  CloudArrowUpRegular,
  SignatureRegular,
  SendRegular,
  CheckmarkCircleRegular,
  CheckmarkCircleFilled,
  DismissCircleRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import {
  TaxReturnStatus,
  StatusChange,
  statusDisplayNames,
  statusColors,
} from '../types';

interface StatusTimelineProps {
  statusHistory: StatusChange[];
  currentStatus: TaxReturnStatus;
  showAllStages?: boolean;
  orientation?: 'vertical' | 'horizontal';
  compact?: boolean;
}

const workflowStages: TaxReturnStatus[] = [
  'scheduled',
  'pending_resolution',
  'documents_pending',
  'ready_to_prepare',
  'in_preparation',
  'in_review',
  'needs_corrections',
  'ready_for_signature',
  'waiting_for_payment',
  'ready_for_efile',
  'e_filed',
  'completed',
];

const stageIcons: Record<TaxReturnStatus, React.ReactNode> = {
  scheduled: <CalendarRegular />,
  pending_resolution: <WarningRegular />,
  documents_pending: <DocumentRegular />,
  ready_to_prepare: <ClipboardTaskListLtrRegular />,
  in_preparation: <EditRegular />,
  in_review: <EyeRegular />,
  needs_corrections: <ErrorCircleRegular />,
  ready_for_efile: <CloudArrowUpRegular />,
  ready_for_signature: <SignatureRegular />,
  waiting_for_payment: <MoneyRegular />,
  e_filed: <SendRegular />,
  completed: <CheckmarkCircleRegular />,
  inactive: <DismissCircleRegular />,
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  horizontalContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '0',
    overflowX: 'auto',
    paddingBottom: '8px',
  },
  stage: {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'relative',
    paddingLeft: '28px',
    paddingBottom: '16px',
    minHeight: '50px',
  },
  horizontalStage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    minWidth: '100px',
    paddingTop: '28px',
  },
  compactStage: {
    paddingBottom: '8px',
    minHeight: '32px',
  },
  iconContainer: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    zIndex: 1,
  },
  horizontalIconContainer: {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    zIndex: 1,
  },
  completed: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  current: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  pending: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  line: {
    position: 'absolute',
    left: '11px',
    top: '24px',
    bottom: '0',
    width: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  horizontalLine: {
    position: 'absolute',
    top: '11px',
    left: '50%',
    right: '-50%',
    height: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  lineCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  content: {
    flex: 1,
  },
  horizontalContent: {
    textAlign: 'center',
    marginTop: '8px',
  },
  stageName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    marginBottom: '2px',
  },
  stageNameCurrent: {
    color: tokens.colorBrandForeground1,
  },
  stageNameCompleted: {
    color: tokens.colorPaletteGreenForeground1,
  },
  stageNamePending: {
    color: tokens.colorNeutralForeground3,
  },
  details: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  notes: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic',
    marginTop: '4px',
  },
  historyItem: {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'relative',
    paddingLeft: '28px',
    paddingBottom: '16px',
  },
  historyIconContainer: {
    position: 'absolute',
    left: '0',
    top: '2px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
  },
  historyLine: {
    position: 'absolute',
    left: '9px',
    top: '22px',
    bottom: '0',
    width: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
});

export default function StatusTimeline({
  statusHistory,
  currentStatus,
  showAllStages = true,
  orientation = 'vertical',
  compact = false,
}: StatusTimelineProps) {
  const styles = useStyles();

  // Get the index of current status in workflow
  const currentStageIndex = workflowStages.indexOf(currentStatus);

  // Create a map of status to its history entry for quick lookup
  const historyMap = new Map<TaxReturnStatus, StatusChange>();
  statusHistory.forEach((change) => {
    historyMap.set(change.toStatus, change);
  });

  // Determine stage state (completed, current, pending)
  const getStageState = (stage: TaxReturnStatus): 'completed' | 'current' | 'pending' => {
    const stageIndex = workflowStages.indexOf(stage);
    if (stage === currentStatus) return 'current';
    if (historyMap.has(stage)) return 'completed';
    if (stageIndex < currentStageIndex) return 'completed';
    return 'pending';
  };

  if (!showAllStages) {
    // Show only history entries
    const sortedHistory = [...statusHistory].sort(
      (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    );

    return (
      <div className={styles.container}>
        {sortedHistory.map((change, idx) => (
          <div key={idx} className={styles.historyItem}>
            {idx < sortedHistory.length - 1 && (
              <div className={styles.historyLine} />
            )}
            <div
              className={styles.historyIconContainer}
              style={{ backgroundColor: statusColors[change.toStatus], color: 'white' }}
            >
              <CheckmarkCircleFilled />
            </div>
            <div className={styles.content}>
              <div className={styles.stageName} style={{ color: statusColors[change.toStatus] }}>
                {statusDisplayNames[change.toStatus]}
              </div>
              <div className={styles.details}>
                {format(new Date(change.changedAt), 'MMM d, yyyy h:mm a')}
                {change.changedByName && ` by ${change.changedByName}`}
              </div>
              {change.notes && <div className={styles.notes}>{change.notes}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (orientation === 'horizontal') {
    return (
      <div className={styles.horizontalContainer}>
        {workflowStages.map((stage, idx) => {
          const state = getStageState(stage);
          const historyEntry = historyMap.get(stage);
          const isLast = idx === workflowStages.length - 1;

          return (
            <div key={stage} className={styles.horizontalStage}>
              {!isLast && (
                <div
                  className={`${styles.horizontalLine} ${
                    state === 'completed' || state === 'current' ? styles.lineCompleted : ''
                  }`}
                />
              )}
              <Tooltip
                content={
                  historyEntry
                    ? `${format(new Date(historyEntry.changedAt), 'MMM d, yyyy')}${
                        historyEntry.changedByName ? ` by ${historyEntry.changedByName}` : ''
                      }`
                    : statusDisplayNames[stage]
                }
                relationship="label"
              >
                <div
                  className={`${styles.horizontalIconContainer} ${
                    state === 'completed'
                      ? styles.completed
                      : state === 'current'
                      ? styles.current
                      : styles.pending
                  }`}
                  style={
                    state === 'current'
                      ? { backgroundColor: statusColors[stage] }
                      : undefined
                  }
                >
                  {state === 'completed' ? <CheckmarkCircleFilled /> : stageIcons[stage]}
                </div>
              </Tooltip>
              <div className={styles.horizontalContent}>
                <div
                  className={`${styles.stageName} ${
                    state === 'completed'
                      ? styles.stageNameCompleted
                      : state === 'current'
                      ? styles.stageNameCurrent
                      : styles.stageNamePending
                  }`}
                  style={
                    state === 'current'
                      ? { color: statusColors[stage] }
                      : undefined
                  }
                >
                  {statusDisplayNames[stage]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {workflowStages.map((stage, idx) => {
        const state = getStageState(stage);
        const historyEntry = historyMap.get(stage);
        const isLast = idx === workflowStages.length - 1;

        return (
          <div
            key={stage}
            className={`${styles.stage} ${compact ? styles.compactStage : ''}`}
          >
            {!isLast && (
              <div
                className={`${styles.line} ${
                  state === 'completed' || state === 'current' ? styles.lineCompleted : ''
                }`}
              />
            )}
            <div
              className={`${styles.iconContainer} ${
                state === 'completed'
                  ? styles.completed
                  : state === 'current'
                  ? styles.current
                  : styles.pending
              }`}
              style={
                state === 'current'
                  ? { backgroundColor: statusColors[stage] }
                  : undefined
              }
            >
              {state === 'completed' ? <CheckmarkCircleFilled /> : stageIcons[stage]}
            </div>
            <div className={styles.content}>
              <div
                className={`${styles.stageName} ${
                  state === 'completed'
                    ? styles.stageNameCompleted
                    : state === 'current'
                    ? styles.stageNameCurrent
                    : styles.stageNamePending
                }`}
                style={
                  state === 'current'
                    ? { color: statusColors[stage] }
                    : undefined
                }
              >
                {statusDisplayNames[stage]}
              </div>
              {!compact && historyEntry && (
                <>
                  <div className={styles.details}>
                    {format(new Date(historyEntry.changedAt), 'MMM d, yyyy h:mm a')}
                    {historyEntry.changedByName && ` by ${historyEntry.changedByName}`}
                  </div>
                  {historyEntry.notes && (
                    <div className={styles.notes}>{historyEntry.notes}</div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
